import { AfterViewInit, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import * as L from 'leaflet';
import { Branch } from '../../interfaces/branch';
import { EventBranch } from '../../interfaces/event';
import { CommonModule } from '@angular/common';
import { DirectionsSelectDialogComponent } from '../directions-select-dialog/directions-select-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './map.component.html',
  styleUrl: './map.component.scss'
})
export class MapComponent implements AfterViewInit, OnChanges, OnDestroy {
  private map: any;
  private clusterGroup: any;

  @Input() branches: Branch[] = [];
  @Input() eventBranches: EventBranch[] = [];
  @Input() newHop = false;
  @Input() newHopSelectabled: number[] = [];
  @Input() toggleNewHop!: Function;
  @Input() mapHeight = '500px';
  @Input() customCoords?: [number, number];
  @Input() customTitle?: string;
  @Input() customLogoUrl?: string;
  @Input() customGooglePlaceId?: string;
  @Input() spreadOverlapping = true;
  @Input() compact = false;
  @Input() enableClustering = true;

  mapId = 'map-' + Math.random().toString(36).substring(2, 11);

  /** True when mapHeight is a Tailwind class (e.g. 'h-[500px]'), false when it's a raw CSS value (e.g. '100%') */
  get isHeightClass(): boolean {
    return this.mapHeight.includes('h-') || this.mapHeight.includes('h_');
  }

  private markers: { branchId?: number; logoUrl: string; title: string; direction: string; marker: any }[] = [];

  private static markerClusterLoaded = false;

  constructor(private dialog: MatDialog) {}

  // ── Lifecycle ────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['newHopSelectabled']) {
      this.updateMarkersSelection();
    }
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
    }
  }

  async ngAfterViewInit(): Promise<void> {
    await this.loadMarkerClusterPlugin();
    this.initMap();
    const bounds: [number, number][] = [];

    if (this.customCoords) {
      this.addMarker(this.customCoords, this.customTitle ?? '', this.customLogoUrl ?? '', this.customGooglePlaceId ?? '');
      bounds.push(this.customCoords);

    } else if (this.eventBranches?.length > 0) {
      if (this.spreadOverlapping) {
        const items = this.eventBranches
          .filter(eb => eb.branch)
          .map(eb => {
            const b = eb.branch!;
            return {
              coord: [parseFloat(b.latitude as any), parseFloat(b.longitude as any)] as [number, number],
              title: b.brewery ? `${b.brewery.name} (${b.address})` : b.address,
              logo: b.brewery?.logoUrl ?? '',
              placeId: b.google_place_id ?? '',
              breweryName: b.brewery?.name,
              branchId: b.id,
            };
          });

        const withDirections = this.assignMarkerDirections(items);
        withDirections.forEach(item => {
          if (this.newHop) {
            this.addMarkerNewHop(item.coord, item.title, item.logo, item.placeId, item.branchId, item.breweryName, item.direction);
          } else {
            this.addMarker(item.coord, item.title, item.logo, item.placeId, item.breweryName, item.direction);
          }
          bounds.push(item.coord);
        });
      } else {
        this.eventBranches.forEach(eb => {
          const b = eb.branch;
          if (!b) return;
          const coords: [number, number] = [parseFloat(b.latitude as any), parseFloat(b.longitude as any)];
          const title = b.brewery ? `${b.brewery.name} (${b.address})` : b.address;
          const logo = b.brewery?.logoUrl ?? '';
          const placeId = b.google_place_id ?? '';
          this.newHop
            ? this.addMarkerNewHop(coords, title, logo, placeId, b.id, b.brewery?.name)
            : this.addMarker(coords, title, logo, placeId, b.brewery?.name);
          bounds.push(coords);
        });
      }

    } else if (this.branches?.length > 0) {
      if (this.spreadOverlapping) {
        const items = this.branches.map((b: any) => ({
          coord: [parseFloat(b.latitude), parseFloat(b.longitude)] as [number, number],
          title: b.brewery ? `${b.brewery.name} (${b.address})` : b.address,
          logo: b.brewery?.logoUrl ?? '',
          placeId: b.google_place_id ?? '',
          breweryName: b.brewery?.name,
          branchId: b.id,
        }));

        const withDirections = this.assignMarkerDirections(items);
        withDirections.forEach(item => {
          if (this.newHop) {
            this.addMarkerNewHop(item.coord, item.title, item.logo, item.placeId, item.branchId, item.breweryName, item.direction);
          } else {
            this.addMarker(item.coord, item.title, item.logo, item.placeId, item.breweryName, item.direction);
          }
          bounds.push(item.coord);
        });
      } else {
        this.branches.forEach((b: any) => {
          const coords: [number, number] = [parseFloat(b.latitude), parseFloat(b.longitude)];
          const title = b.brewery ? `${b.brewery.name} (${b.address})` : b.address;
          const logo = b.brewery?.logoUrl ?? '';
          const placeId = b.google_place_id ?? '';
          this.newHop
            ? this.addMarkerNewHop(coords, title, logo, placeId, b.id, b.brewery?.name)
            : this.addMarker(coords, title, logo, placeId, b.brewery?.name);
          bounds.push(coords);
        });
      }
    }

    if (bounds.length) {
      this.map.fitBounds(bounds, { padding: [40, 40] });
    }

    // Ensure the map renders correctly when container size is resolved after init
    setTimeout(() => {
      if (this.map) this.map.invalidateSize();
    }, 100);
  }

  // ── Load markercluster plugin ─────────────────────────

  private async loadMarkerClusterPlugin(): Promise<void> {
    if (MapComponent.markerClusterLoaded) return;
    // Assign L to window so the UMD plugin can find it
    (window as any)['L'] = L;
    await import('leaflet.markercluster');
    MapComponent.markerClusterLoaded = true;
  }

  // ── Map init ─────────────────────────────────────────

  private initMap(): void {
    this.map = L.map(this.mapId, {
      center: [9.0, -79.5],
      zoom: 12,
      ...(this.compact ? { zoomControl: false, attributionControl: false } : {}),
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      minZoom: 3,
      attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(this.map);

    // Initialize cluster group
    if (this.enableClustering && (window as any)['L'].markerClusterGroup) {
      this.clusterGroup = (window as any)['L'].markerClusterGroup({
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        spiderfyOnMaxZoom: false,
        removeOutsideVisibleBounds: false,
        animate: true,
        animateAddingMarkers: false,
        maxClusterRadius: 60,
        disableClusteringAtZoom: 17,
        iconCreateFunction: (cluster: any) => this.createClusterIcon(cluster),
      });
      this.map.addLayer(this.clusterGroup);
    }
  }

  // ── Cluster icon ─────────────────────────────────────

  private createClusterIcon(cluster: any): any {
    const count = cluster.getChildCount();
    const childMarkers = cluster.getAllChildMarkers();

    // Check if any child marker is selected
    const hasSelected = childMarkers.some((m: any) => {
      const branchId = (m as any)._branchId;
      return branchId !== undefined && this.newHopSelectabled.includes(branchId);
    });

    // Collect unique logos with their selected state (max 5 around the ring)
    const logoItems: { url: string; selected: boolean }[] = [];
    const seenLogos = new Set<string>();
    for (const m of childMarkers) {
      const logo = (m as any)._logoUrl;
      if (logo && !seenLogos.has(logo)) {
        seenLogos.add(logo);
        const branchId = (m as any)._branchId;
        const isSelected = branchId !== undefined && this.newHopSelectabled.includes(branchId);
        logoItems.push({ url: logo, selected: isSelected });
        if (logoItems.length >= 5) break;
      }
    }

    const logoSize = 32;
    const containerSize = 80;
    const radius = 26; // distance from center to logo center
    const center = containerSize / 2;

    // Position logos in a circle around the center
    let logosHtml = '';
    const angleStep = 360 / logoItems.length;
    logoItems.forEach((item, i) => {
      const angleDeg = -90 + i * angleStep; // start from top
      const angleRad = angleDeg * (Math.PI / 180);
      const x = center + radius * Math.cos(angleRad) - logoSize / 2;
      const y = center + radius * Math.sin(angleRad) - logoSize / 2;
      const selectedClass = item.selected ? 'map-cluster-ring__logo--selected' : '';
      logosHtml += `<img src="${item.url}" alt="" class="map-cluster-ring__logo ${selectedClass}" style="left:${x.toFixed(1)}px;top:${y.toFixed(1)}px;width:${logoSize}px;height:${logoSize}px;" />`;
    });

    return new L.DivIcon({
      className: '',
      html: `
        <div class="map-cluster-ring ${hasSelected ? 'map-cluster-ring--selected' : ''}" style="width:${containerSize}px;height:${containerSize}px;">
          ${logosHtml}
          <span class="map-cluster-ring__count">${count}</span>
        </div>`,
      iconSize: [containerSize, containerSize],
      iconAnchor: [containerSize / 2, containerSize / 2],
    });
  }

  // ── Marker icon ──────────────────────────────────────

  private createMarkerIcon(logoUrl: string, branchId?: number, title?: string, direction: string = 'top'): any {
    const isSelected = branchId !== undefined && this.newHopSelectabled.includes(branchId);

    if (logoUrl) {
      if (this.spreadOverlapping) {
        const logoSize = 40;
        const pinLength = 12;
        const totalH = logoSize + pinLength;
        const totalW = logoSize;

        let iconSize: [number, number];
        let iconAnchor: [number, number];
        let pinClass: string;

        switch (direction) {
          case 'bottom':
            iconSize = [totalW, totalH];
            iconAnchor = [totalW / 2, 0];
            pinClass = 'map-marker-spread--bottom';
            break;
          case 'left':
            iconSize = [totalH, totalW];
            iconAnchor = [totalH, totalW / 2];
            pinClass = 'map-marker-spread--left';
            break;
          case 'right':
            iconSize = [totalH, totalW];
            iconAnchor = [0, totalW / 2];
            pinClass = 'map-marker-spread--right';
            break;
          default:
            iconSize = [totalW, totalH + 6];
            iconAnchor = [totalW / 2, totalH + 6];
            pinClass = '';
            break;
        }

        return new L.DivIcon({
          className: '',
          html: `
            <div class="map-marker-spread ${pinClass} ${isSelected ? 'map-marker-spread--selected' : ''}">
              <div class="map-marker-spread__logo">
                <img src="${logoUrl}" alt="${title ?? ''}" />
              </div>
              <div class="map-marker-spread__pin"></div>
            </div>`,
          iconSize: iconSize,
          iconAnchor: iconAnchor,
        });
      }

      return new L.DivIcon({
        className: '',
        html: `
          <div class="map-marker ${isSelected ? 'map-marker--selected' : ''}">
            <div class="map-marker__logo">
              <img src="${logoUrl}" alt="${title ?? ''}" />
            </div>
            <div class="map-marker__pin"></div>
          </div>`,
        iconSize: [56, 68],
        iconAnchor: [28, 68],
        popupAnchor: [0, -70],
      });
    }

    return new L.DivIcon({
      className: '',
      html: `
        <div class="map-pin-fallback ${isSelected ? 'map-pin-fallback--selected' : ''}">
          <svg viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 0C5.373 0 0 5.373 0 12c0 8.25 12 20 12 20S24 20.25 24 12C24 5.373 18.627 0 12 0z"/>
            <circle cx="12" cy="12" r="4.5" fill="white" opacity="0.9"/>
          </svg>
        </div>`,
      iconSize: [28, 36],
      iconAnchor: [14, 36],
    });
  }

  // ── Update selection ─────────────────────────────────

  private updateMarkersSelection(): void {
    this.markers.forEach(m => {
      m.marker.setIcon(this.createMarkerIcon(m.logoUrl, m.branchId, m.title, m.direction));
    });
    // Refresh cluster icons to reflect selected state
    if (this.clusterGroup) {
      this.clusterGroup.refreshClusters();
    }
  }

  // ── Add markers ──────────────────────────────────────

  private addToMap(marker: L.Marker): void {
    if (this.enableClustering && this.clusterGroup) {
      this.clusterGroup.addLayer(marker);
    } else {
      marker.addTo(this.map);
    }
  }

  private getZIndexForDirection(direction: string): number {
    // 'top' markers should render above 'bottom' ones to avoid overlap
    switch (direction) {
      case 'top': return 400;
      case 'left': return 300;
      case 'right': return 300;
      case 'bottom': return 200;
      default: return 400;
    }
  }

  addMarker(location: [number, number], title: string, logoUrl: string, google_place_id: string, breweryName?: string, direction: string = 'top') {
    if (!this.map) return;

    const icon = this.createMarkerIcon(logoUrl, undefined, title, direction);
    const marker = new L.Marker(location, { icon, zIndexOffset: this.getZIndexForDirection(direction) });
    (marker as any)._logoUrl = logoUrl;

    marker.on('click', () => {
      this.map.panTo(location, { animate: true, duration: 0.5 });
      if (!this.compact) {
        this.openInMaps(location[0], location[1], title, google_place_id, breweryName);
      }
    });

    marker.on('mouseover', () => marker.setZIndexOffset(1000));
    marker.on('mouseout',  () => marker.setZIndexOffset(this.getZIndexForDirection(direction)));

    this.addToMap(marker);
  }

  addMarkerNewHop(location: [number, number], title: string, logoUrl: string, google_place_id: string, branchId?: number, breweryName?: string, direction: string = 'top') {
    if (!this.map) return;

    const marker = new L.Marker(location, {
      icon: this.createMarkerIcon(logoUrl, branchId, title, direction),
      zIndexOffset: this.getZIndexForDirection(direction)
    });
    (marker as any)._logoUrl = logoUrl;
    (marker as any)._branchId = branchId;

    this.markers.push({ branchId, logoUrl, title, direction, marker });

    marker.on('click', () => {
      this.map.panTo(location, { animate: true, duration: 0.5 });
      if (branchId && this.toggleNewHop) this.toggleNewHop(branchId);
    });

    marker.on('mouseover', () => marker.setZIndexOffset(1000));
    marker.on('mouseout',  () => marker.setZIndexOffset(this.getZIndexForDirection(direction)));

    this.addToMap(marker);
  }

  // ── Spread overlapping markers ─────────────────────────

  private assignMarkerDirections(
    items: { coord: [number, number]; logo: string; title: string; placeId: string; breweryName?: string; branchId?: number }[]
  ): { coord: [number, number]; logo: string; title: string; placeId: string; breweryName?: string; branchId?: number; direction: string }[] {
    const minDistance = 0.0004; // ~50m at equator
    const result = items.map(item => ({ ...item, direction: 'top' }));
    const processed = new Set<number>();

    for (let i = 0; i < result.length; i++) {
      if (processed.has(i)) continue;

      const cluster: number[] = [i];
      for (let j = i + 1; j < result.length; j++) {
        if (processed.has(j)) continue;
        const latDiff = Math.abs(result[i].coord[0] - result[j].coord[0]);
        const lngDiff = Math.abs(result[i].coord[1] - result[j].coord[1]);
        if (latDiff < minDistance && lngDiff < minDistance) {
          cluster.push(j);
        }
      }

      if (cluster.length <= 1) continue;

      // Calculate center of the cluster
      const centerLat = cluster.reduce((sum, idx) => sum + result[idx].coord[0], 0) / cluster.length;
      const centerLng = cluster.reduce((sum, idx) => sum + result[idx].coord[1], 0) / cluster.length;

      // Each marker points away from center based on its relative position
      cluster.forEach(idx => {
        const latOffset = result[idx].coord[0] - centerLat; // positive = north (up on map)
        const lngOffset = result[idx].coord[1] - centerLng; // positive = east (right on map)

        if (Math.abs(latOffset) >= Math.abs(lngOffset)) {
          // Vertical dominant: north → top, south → bottom
          result[idx].direction = latOffset >= 0 ? 'top' : 'bottom';
        } else {
          // Horizontal dominant: east → right, west → left
          result[idx].direction = lngOffset >= 0 ? 'right' : 'left';
        }

        processed.add(idx);
      });
    }

    return result;
  }

  // ── Navigation ───────────────────────────────────────

  openInMaps(lat: number, lng: number, name = '', google_place_id?: string, breweryName?: string) {
    const enc = encodeURIComponent(name);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const urls = {
      google: google_place_id
        ? `https://www.google.com/maps/search/?api=1&query=${enc}&query_place_id=${google_place_id}`
        : `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
      waze: breweryName
        ? `https://waze.com/ul?q=${enc}&ll=${lat},${lng}&navigate=yes`
        : `https://waze.com/ul?ll=${lat},${lng}&navigate=yes&zoom=17`,
      uber: isMobile
        ? `https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[latitude]=${lat}&dropoff[longitude]=${lng}`
        : `https://m.uber.com/?action=setPickup&pickup=my_location&dropoff[latitude]=${lat}&dropoff[longitude]=${lng}`,
    };
    this.dialog.open(DirectionsSelectDialogComponent, { data: urls });
  }
}
